class FreeShippingMeter extends HTMLElement {
  constructor() {
    super();
    this._observer = null;
    this._debounceTimer = null;
  }

  // Text fragments from your theme (kept compatible)
  static freeShippingText = window.free_shipping_text?.free_shipping_message || '';
  static freeShippingText1 = window.free_shipping_text?.free_shipping_message_1 || '';
  static freeShippingText2 = window.free_shipping_text?.free_shipping_message_2 || '';
  static freeShippingText3 = window.free_shipping_text?.free_shipping_message_3 || '';
  static freeShippingText4 = window.free_shipping_text?.free_shipping_message_4 || '';

  static classLabel1 = 'progress-30';
  static classLabel2 = 'progress-60';
  static classLabel3 = 'progress-100';

  connectedCallback() {
    this.freeShippingEligible = 0;

    this.progressBar = this.querySelector('[data-shipping-progress]');
    this.messageElement = this.querySelector('[data-shipping-message]');
    this.textEnabled = this.progressBar?.dataset.textEnabled === 'true';
    this.shipVal = window.free_shipping_text?.free_shipping_1;

    this.progressMeter = this.querySelector('[data-free-shipping-progress-meter]');

    // Update on basic interactions
    this.addEventListener('change', () => this.scheduleUpdate());
    this.addEventListener('input', () => this.scheduleUpdate());

    // Update on load
    this.scheduleUpdate();

    // Catch theme AJAX cart updates (common events across themes/apps)
    window.addEventListener('cart:updated', () => this.scheduleUpdate());
    window.addEventListener('cart:refresh', () => this.scheduleUpdate());
    document.addEventListener('cart:updated', () => this.scheduleUpdate());
    document.addEventListener('cart:refresh', () => this.scheduleUpdate());

    // Observe DOM changes inside the component (AJAX cart redraws can replace inner HTML)
    this._observer = new MutationObserver(() => this.scheduleUpdate());
    this._observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
  }

  scheduleUpdate() {
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this.updateFromCart(), 120);
  }

  // Always normalise goal to cents, defaulting to $99 if theme value is missing/odd.
  getGoalCents() {
    // Hard default: $99.00
    let goalCents = 9900;

    // If theme provides a value, use it safely
    const raw = window.free_shipping_price;

    if (raw !== undefined && raw !== null && raw !== '') {
      // supports "99", "99.00", "9900"
      const num = Number(String(raw).replace(/[^0-9.]/g, ''));
      if (!Number.isNaN(num) && num > 0) {
        // If it's small (e.g., 99 or 99.00) treat as dollars; if large (e.g., 9900) treat as cents
        goalCents = num < 1000 ? Math.round(num * 100) : Math.round(num);
      }
    }

    return goalCents;
  }

  async fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Failed to fetch cart.js');
    return res.json();
  }

  async updateFromCart() {
    // Re-query in case theme re-rendered inner markup
    this.progressBar = this.querySelector('[data-shipping-progress]');
    this.messageElement = this.querySelector('[data-shipping-message]');
    this.progressMeter = this.querySelector('[data-free-shipping-progress-meter]');
    this.textEnabled = this.progressBar?.dataset.textEnabled === 'true';

    if (!this.progressBar || !this.progressMeter || !this.messageElement) return;

    let cart;
    try {
      cart = await this.fetchCart();
    } catch (e) {
      // If cart fetch fails, don’t break UI
      return;
    }

    const goalCents = this.getGoalCents();
    const cartTotalCents = Number(cart?.total_price || 0);

    // Percent (0..100) based on cart.total_price
    let percent = 0;
    if (goalCents > 0) {
      percent = (cartTotalCents * 100) / goalCents;
      percent = Math.max(0, Math.min(100, percent));
    }

    const classLabel = this.getClassLabel(percent);
    const text = this.getText(cartTotalCents, goalCents);

    this.setProgressWidthAndText(percent, text, classLabel);
  }

  getText(cartTotalCents, goalCents) {
    // cartTotalCents and goalCents are cents
    if (cartTotalCents <= 0) {
      this.progressBar.classList.add('progress-hidden');
      return (
        '<span>' +
        FreeShippingMeter.freeShippingText +
        ' ' +
        this.formatMoney(goalCents) +
        '!</span>'
      );
    }

    this.progressBar.classList.remove('progress-hidden');

    if (cartTotalCents >= goalCents) {
      this.freeShippingEligible = 1;
      return FreeShippingMeter.freeShippingText1;
    }

        const remainingCents = Math.max(0, goalCents - cartTotalCents);

return `<span class="text">${this.formatMoney(remainingCents)}</span> away from free shipping`;
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, window.money_format);
    }
    // Fallback if Shopify.formatMoney isn't available
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  }

  getClassLabel(percent) {
    if (percent === 0) return 'none';
    if (percent <= 30) return FreeShippingMeter.classLabel1;
    if (percent <= 60) return FreeShippingMeter.classLabel2;
    if (percent < 100) return FreeShippingMeter.classLabel3;
    return 'progress-free';
  }

  resetProgressClass(classLabel) {
    this.progressBar.classList.remove('progress-30', 'progress-60', 'progress-100', 'progress-free');
    this.progressBar.classList.add(classLabel);
  }

  setProgressWidthAndText(percent, text, classLabel) {
    // Small delay keeps your existing visual behaviour consistent
    setTimeout(() => {
      this.resetProgressClass(classLabel);

      this.progressMeter.style.width = `${percent}%`;

      if (this.textEnabled) {
        const textEl = this.progressMeter.querySelector('.text');
        if (textEl) textEl.innerHTML = `${Math.round(percent)}%`;
      }

      this.messageElement.innerHTML = text;

      // Currency conversion hook (kept from your original code)
      if (
        (window.show_multiple_currencies && typeof Currency !== 'undefined' && Currency.currentCurrency !== shopCurrency) ||
        window.show_auto_currency
      ) {
        Currency.convertAll(
          window.shop_currency,
          $('#currencies .active').attr('data-currency'),
          'span.money',
          'money_format'
        );
      }
    }, 150);
  }
}

window.addEventListener('load', () => {
  if (!customElements.get('free-shipping-component')) {
    customElements.define('free-shipping-component', FreeShippingMeter);
  }
});